import { outreach } from '@round/api';
import { useOutreachRequestResultsInfinite } from '@round/creatorbase-helpers/api-hooks';
import { useTiktokUserImageQueriesByIds } from '@round/platforms/tiktok';

type Params = Omit<outreach.GetOutreachResultsParams, 'page' | 'page_size'>;

export function useOutreachResults(params: Params) {
    // An action writes its row into the loaded pages rather than refetching them, which leaves the
    // list stale on purpose: the tab filters on the status the action just changed, so a reload
    // would take the row away from whoever pressed the button. Coming back to the window is not
    // that person asking for a new list, so it is the one refetch this table does not want.
    const list = useOutreachRequestResultsInfinite(params, { refetchOnWindowFocus: false });

    const imageQueries = useTiktokUserImageQueriesByIds(
        list.pages.map((page) => page.results.map((result) => result.user_id))
    );

    const avatarUrlByUserId = new Map(
        imageQueries
            .flatMap((query) => query.data ?? [])
            .map((image) => [image.user_id, image.avatar_thumb.cached_url || image.avatar_thumb.original_url])
    );

    return {
        rows: list.results,
        count: list.count,
        isLoading: list.isLoading,
        hasNextPage: list.hasNextPage,
        isFetchingNextPage: list.isFetchingNextPage,
        isFetchNextPageError: list.isFetchNextPageError,
        fetchNextPage: list.fetchNextPage,
        isError: list.isError,
        errorMessage: list.errorMessage,
        getAvatarUrl: (userId: number) => avatarUrlByUserId.get(userId),
        areAvatarsLoading: imageQueries.some((query) => query.isLoading),
    };
}
